import React, { useState } from 'react';
import {
  CompactTable,
  Group,
  Page,
  PermissionGuard,
  useFilters,
  usePageQuery,
  useUserinfo,
  defaultResponseHandler,
} from '@mss/mss-component-library';
import PageSidebar from 'jsx/common/FilterSidebar';
import Button from '../../../../common/Button';
import Breadcrumbs from '../../../../common/Breadcrumbs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useHistory, useRouteMatch } from 'react-router-dom';
import columns from './columns';
import AddGroupModal from '../Add/Modal';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import Fetch from '../../../../common/util/_fetch';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { sanitizedUrl } from '../../../../asset/util/sanitize';

const filterKeys: (keyof Group)[] = [];
const searchKeys: (keyof Group)[] = ['name'];

const customHeadingLabel = (t: TFunction) => (key: string) => {
  if (key === 'name') return t('Name');
  return key;
};

const PAGE_SIZE = 10;

export default function List() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const [showAddGroupModal, setShowModal] = useState(false);
  const [t] = useTranslation(['authorization', 'common']);
  const queryClient = useQueryClient();
  const { data: groups, isFetching } = useQuery({
    queryKey: ['groups'],
    initialData: [],
    queryFn: async () =>
      (
        await defaultResponseHandler<Group[]>({
          t,
          request: Fetch.get('/api/group/'),
        })
      ).data ?? [],
  });

  const [availableFilters, toggleFilter, filteredData, search] = useFilters(
    groups,
    filterKeys,
    searchKeys,
    undefined,
    customHeadingLabel(t)
  );
  const [page, goToPage] = usePageQuery(filteredData.length / PAGE_SIZE);
  const [hasPermission] = useUserinfo();
  return (
    <PermissionGuard
      codename={['add_group', 'view_group', 'change_group', 'delete_group']}
    >
      <Page
        breadCrumbs={<Breadcrumbs />}
        padding={false}
        actions={
          <PermissionGuard codename="add_group">
            <Button
              onClick={() => {
                if (!showAddGroupModal) setShowModal(true);
              }}
              data-ta="newGroupButton"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1" />{' '}
              {t('ADD GROUP')}
            </Button>
          </PermissionGuard>
        }
        main={
          <>
            <PermissionGuard codename="view_group">
              <CompactTable
                dataSource={filteredData}
                loading={isFetching}
                columns={columns(hasPermission, queryClient, t)}
                rowKey={'id'}
                bordered={false}
                pagination={{
                  current: page,
                  showQuickJumper: true,
                  showSizeChanger: true,
                  total: filteredData.length,
                  showTotal: (total: number, range: any[]) =>
                    t('(RANGE1)-(RANGE2) of (TOTAL) items', {
                      range1: range[0],
                      range2: range[1],
                      TOTAL: total,
                    }),
                }}
                onChange={(pagination: any) => {
                  if (page !== pagination.current) goToPage(pagination.current);
                }}
                onRow={(record: any) => {
                  return {
                    onClick: () =>
                      history.push(
                        `${sanitizedUrl(url)}/${record.id}/settings`
                      ),
                  };
                }}
              />
            </PermissionGuard>
            {showAddGroupModal && (
              <AddGroupModal closeModal={() => setShowModal(false)} />
            )}
          </>
        }
        sidebar={
          <PermissionGuard codename="view_group">
            <PageSidebar<Group>
              availableFilters={availableFilters}
              toggleFilter={toggleFilter}
              search={search}
            />
          </PermissionGuard>
        }
      />
    </PermissionGuard>
  );
}
